import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ArrowRight, Sparkles } from 'lucide-react';
import type { OnboardingStatus } from '@/types/photographer';

interface OnboardingWizardProps {
  status: OnboardingStatus;
  firstName?: string;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ status, firstName }) => {
  const pct = status.total === 0 ? 0 : Math.round((status.completed / status.total) * 100);
  const nextStep = status.steps.find(s => !s.done);

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="rounded-t-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-white">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <h1 className="text-lg font-semibold">
              {firstName ? `Welcome, ${firstName}!` : 'Welcome to PixelShare'}
            </h1>
          </div>
          <p className="mt-1 text-sm text-blue-100">
            Let&apos;s get your photographer account set up. A few quick steps and you&apos;ll be ready to sell.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <div className="h-2 flex-1 rounded-full bg-white/20">
              <div
                className="h-2 rounded-full bg-white transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-medium tabular-nums">
              {status.completed}/{status.total}
            </span>
          </div>
        </div>

        <ul className="divide-y divide-gray-100">
          {status.steps.map(step => (
            <li
              key={step.key}
              className="flex items-center justify-between gap-4 px-6 py-4"
            >
              <div className="flex items-center gap-3">
                {step.done ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-green-500" />
                ) : (
                  <Circle className="h-5 w-5 shrink-0 text-gray-300" />
                )}
                <span
                  className={
                    step.done
                      ? 'text-sm text-gray-400 line-through'
                      : 'text-sm font-medium text-gray-900'
                  }
                >
                  {step.label}
                </span>
              </div>
              {!step.done && (
                <Link
                  to={step.cta}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  {step === nextStep ? 'Start' : 'Go'}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </li>
          ))}
        </ul>

        <div className="rounded-b-2xl border-t border-gray-100 bg-gray-50 px-6 py-3 text-center text-xs text-gray-500">
          Finish setup to unlock your full dashboard.
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
